/**
 * web-push.ts — Đăng ký thiết bị này nhận thông báo đẩy (Web Push qua Firebase Cloud Messaging) để được báo tin khách
 * kể cả khi đóng ứng dụng. Cùng cơ chế với app Evotech Partner.
 *
 * Luồng: quyền thông báo đã cấp + service worker (public/sw.js) đang chạy → lấy cấu hình Firebase từ máy chủ
 * (GET /notifications/push/config — nên KHÔNG cần build lại frontend khi đổi khoá) → `getToken` của Firebase, dùng CHÍNH service worker
 * của eCDP (không cần thêm firebase-messaging-sw.js) → gửi token lên máy chủ gắn với người đang đăng nhập.
 * Máy chủ chỉ gửi cho người đã bật "Thông báo trình duyệt"; service worker tự bỏ qua khi đang có cửa sổ ứng dụng nhìn thấy + focus.
 * Thư viện Firebase chỉ tải khi thật sự cần (import động) nên không làm nặng lần mở app đầu.
 *
 * Không làm được khi: trình duyệt không hỗ trợ (iPhone chưa cài ứng dụng ra màn hình chính), không có service worker (bản dev /
 * đã tắt `VITE_DISABLE_SW`), máy chủ chưa cấu hình Firebase. Mọi hàm nuốt lỗi và trả mã kết quả — Web Push chỉ là lớp bổ sung.
 * KHÔNG ghi token ra console.
 */
import { api } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'

export type PushResult = 'ok' | 'unsupported' | 'no-permission' | 'disabled' | 'error'

export interface FirebaseWebConfig {
  apiKey: string
  authDomain?: string
  projectId: string
  storageBucket?: string
  messagingSenderId: string
  appId: string
}
export interface PushClientConfig { enabled: boolean; firebase?: FirebaseWebConfig; vapidKey?: string }

/** Cấu hình máy chủ trả về có đủ để khởi tạo Firebase không. */
export function usableConfig(c: PushClientConfig | null | undefined): { firebase: FirebaseWebConfig; vapidKey: string } | null {
  if (!c?.enabled || !c.vapidKey || !c.firebase) return null
  const f = c.firebase
  if (!f.apiKey || !f.projectId || !f.messagingSenderId || !f.appId) return null
  return { firebase: f, vapidKey: c.vapidKey }
}

// ── Token của thiết bị này (giữ để huỷ khi tắt / đăng xuất) ──────────────
const TOKEN_KEY = 'ecdp:fcm-token'
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
const safeLocal = (): Store | null => {
  try { return typeof window !== 'undefined' ? window.localStorage : null } catch { return null }
}
export function readToken(store: Store | null = safeLocal()): string | null {
  try { return store?.getItem(TOKEN_KEY) || null } catch { return null }
}
export function saveToken(token: string, store: Store | null = safeLocal()) {
  try { store?.setItem(TOKEN_KEY, token) } catch { /* chế độ riêng tư */ }
}
export function clearToken(store: Store | null = safeLocal()) {
  try { store?.removeItem(TOKEN_KEY) } catch { /* bỏ qua */ }
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null
  } catch {
    return null
  }
}

/** Tải Firebase (import động) và dựng messaging. Trình duyệt không hỗ trợ FCM → null. */
async function loadMessaging(firebase: FirebaseWebConfig) {
  const [app, msg] = await Promise.all([import('firebase/app'), import('firebase/messaging')])
  if (!(await msg.isSupported())) return null
  const instance = app.getApps().find((a) => a.name === 'ecdp-push') ?? app.initializeApp(firebase, 'ecdp-push')
  return { messaging: msg.getMessaging(instance), getToken: msg.getToken, deleteToken: msg.deleteToken }
}

async function fetchConfig() {
  const { data } = await api.get<PushClientConfig>('/notifications/push/config')
  return usableConfig(data)
}

/**
 * Gộp các lần gọi chạy CÙNG LÚC thành một: trong lúc bật, hộp xin quyền, công tắc ở Cài đặt và NotificationCenter có thể cùng
 * gọi `enablePush` trong vài trăm mili-giây — hai lần `getToken` song song làm Firebase tạo hai token (một cái bị thay thế ngay,
 * máy chủ còn nhớ cả hai và phải dọn lúc gửi). Gọi trong lúc một lần đang chạy → dùng lại kết quả của lần đó.
 */
export function singleFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null
  return () => {
    inFlight ??= fn().finally(() => { inFlight = null })
    return inFlight
  }
}

/** Đăng ký (hoặc làm mới) thiết bị này. Gọi lại nhiều lần an toàn (máy chủ upsert theo token; lần gọi chồng nhau được gộp). */
export const enablePush: () => Promise<PushResult> = singleFlight(doEnablePush)

async function doEnablePush(): Promise<PushResult> {
  if (!pushSupported()) return 'unsupported'
  if (Notification.permission !== 'granted') return 'no-permission'
  try {
    const reg = await registration()
    if (!reg) return 'unsupported'
    const cfg = await fetchConfig()
    if (!cfg) return 'disabled'
    const fb = await loadMessaging(cfg.firebase)
    if (!fb) return 'unsupported'
    const token = await fb.getToken(fb.messaging, { vapidKey: cfg.vapidKey, serviceWorkerRegistration: reg })
    if (!token) return 'error'
    await api.post('/notifications/push/subscribe', { token })
    saveToken(token)
    return 'ok'
  } catch {
    return 'error'
  }
}

/** Bỏ đăng ký thiết bị này (tắt thông báo / đăng xuất — máy dùng chung không được nhận tin của người trước). */
export async function disablePush(): Promise<void> {
  const token = readToken()
  if (!token) return
  try {
    await api.post('/notifications/push/unsubscribe', { token }).catch(() => {})
    clearToken()
    // Huỷ luôn token phía Firebase (best-effort) để không còn đăng ký treo ở trình duyệt.
    if (pushSupported()) {
      const cfg = await fetchConfig().catch(() => null)
      const fb = cfg ? await loadMessaging(cfg.firebase) : null
      if (fb) await fb.deleteToken(fb.messaging).catch(() => {})
    }
  } catch { /* bỏ qua */ }
}

/** Thiết bị này đang có đăng ký đẩy chưa (để hiện trạng thái ở Cài đặt cá nhân). */
export async function isPushSubscribed(): Promise<boolean> {
  return pushSupported() && Notification.permission === 'granted' && readToken() !== null
}

export const PUSH_NOTE: Record<PushResult, string> = {
  get ok() { return i18n.t('sharedMisc:webPush.ok') },
  get unsupported() { return i18n.t('sharedMisc:webPush.unsupported') },
  'no-permission': '',
  get disabled() { return i18n.t('sharedMisc:webPush.disabled') },
  get error() { return i18n.t('sharedMisc:webPush.error') },
}
